// =============================================================================
// StageCard: one application on the Track tab's stage board. Reads straight
// off apps/throughline/src/lib/api.ts's Application — no reshaping, no
// invented fields. Silence tiers render EXACTLY the server's own
// silenceDays/nudgeTier/closeOffered (W1-7) — this component never re-derives
// "how quiet" from appliedAt itself (the server's nudge_policy is retunable
// data; hardcoding a threshold here would let this card silently disagree
// with the backend the moment Jason retunes the policy).
//
// Draggable via native HTML5 drag (draggable attribute + onDragStart), the
// SAME pattern AchievementCard.tsx already established for the Refine tab's
// corpus canvas (see that file's header for why native drag over @dnd-kit).
// The stage-move SELECT beside the drag handle is the brief's own "buttons"
// fallback for whenever a drag isn't practical (keyboard-only use, a touch
// device without a drop target visible) — mirrors apps/web/src/pages/
// Tracker.tsx's own <select> per-card mover (Tracker.tsx:94-106) rather than
// inventing a new interaction, since PATCH /applications/:id supports moving
// to ANY status directly (planTransition has no "valid transitions" state
// machine — see applicationTransition.ts), so a plain select is a faithful,
// not a simplified, mirror of what the backend actually allows.
//
// TRACK-FIDELITY (2026-07-06, owner QA: "once tracked... you don't get the
// same details... it loses all of it"). Root cause traced to TWO compounding
// gaps: (1) this component had ZERO navigate/Link — a tracked card was a
// dead end, no click target routed back to the rich JobDetail page it came
// from; (2) applications.routes.ts's GET / never resolved jobId through the
// shared canonical loader (loadJobForUser), so even a resolvable id had no
// served signal saying so. The fix: the TITLE/COMPANY block (only — not the
// whole card) is now a Link to /jobs/:jobId, mirroring Jobs.tsx:101's own
// `<Link to={`/jobs/${job.id}`}>` house pattern, when the server's new
// `jobAvailable` flag says the id still resolves. Deliberately NOT wrapping
// the entire <Card> in an <a>: this card already holds several independently
// interactive controls (drag handle, stage <select>, draft-follow-up button,
// the notes editor's textarea + Save/Cancel) — nesting all of that inside an
// anchor would hijack every one of those clicks into a navigation instead of
// its own action, a real usability regression, not a fix. When the job is
// NOT available (a genuine orphan — gone from both the legacy table and the
// lake for this user), the title/company render as plain text plus one
// honest line — never a dead link disguised as live, never a fabricated
// detail (NO INVENTION). Every other affordance on this card (drag, stage
// move, notes, follow-up draft) is UNCHANGED — additive only, per the brief.
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  APPLICATION_STATUSES,
  type Application,
  type ApplicationStatus,
  type DraftOutreachResult,
} from '../lib/api';
import {
  applicationJobHref,
  shouldOfferClose,
  silenceLabel,
  TRACKER_STAGE_DISPLAY_LABEL,
} from '../lib/applicationBoard';
import { Card } from './Card';
import clsx from './clsx';
import { VoiceTextarea } from './VoiceTextarea';
import styles from './StageCard.module.css';

export interface StageCardProps {
  application: Application;
  dragging?: boolean;
  pending?: boolean;
  onDragStart?: (id: string) => void;
  onDragEnd?: () => void;
  onMoveStage: (id: string, toStatus: ApplicationStatus) => void;
  /** True only while THIS card's follow-up draft is in flight. */
  draftingFollowUp?: boolean;
  onDraftFollowUp: (jobId: string) => void;
  /** The last-received draft result for THIS card (success, refusal, or a
   *  Gmail-connection failure) — null before the button has ever been
   *  pressed. Kept per-card by the caller (Tracker.tsx) so drafting one
   *  application's follow-up never overwrites another's visible result. */
  draftResult?: DraftOutreachResult | null;
  /** GAP 4 — save (or clear) this application's own note. PATCH
   *  /applications/:id already accepts `notes` independently of a stage move
   *  (applications.routes.ts:253-312); this wires the affordance that never
   *  existed on any screen. Rejects (throws) on a failed save so this card
   *  can keep the editor open with the user's draft intact rather than
   *  silently discarding it — see this component's own handleSaveNote. */
  onSaveNotes: (id: string, notes: string) => Promise<void>;
  /** True only while THIS card's note save is in flight (useApplicationsBoard's
   *  notesSavingId === application.id) — mirrors the `pending` prop's own
   *  per-card scoping for stage moves. */
  savingNotes?: boolean;
}

export function StageCard({
  application,
  dragging = false,
  pending = false,
  onDragStart,
  onDragEnd,
  onMoveStage,
  draftingFollowUp = false,
  onDraftFollowUp,
  draftResult = null,
  onSaveNotes,
  savingNotes = false,
}: StageCardProps) {
  // GAP 4 — collapsed by default (house rule: no long forms visible until
  // asked for). Starts expanded ONLY when a real note already exists, so the
  // user sees what they wrote without an extra tap; a card with no note yet
  // starts collapsed behind "Add a note". `noteDraft` is seeded from the
  // served `application.notes` (never invented) and only diverges from it
  // while the user is actively editing — a successful save re-seeds from the
  // server's own returned row (via the application prop updating), so the
  // draft can never silently drift from what's actually persisted.
  const hasExistingNote = !!application.notes && application.notes.trim().length > 0;
  const [noteExpanded, setNoteExpanded] = useState(hasExistingNote);
  const [noteDraft, setNoteDraft] = useState(application.notes ?? '');
  const [noteSaveError, setNoteSaveError] = useState<string | null>(null);
  const [noteJustSaved, setNoteJustSaved] = useState(false);

  async function handleSaveNote() {
    setNoteSaveError(null);
    try {
      await onSaveNotes(application.id, noteDraft);
      setNoteJustSaved(true);
      setTimeout(() => setNoteJustSaved(false), 1600);
    } catch (err) {
      setNoteSaveError(err instanceof Error ? err.message : 'Could not save that note — try again.');
    }
  }

  const silence = silenceLabel(application);
  const offerClose = shouldOfferClose(application);
  const title = application.jobTitle ?? 'Untitled role';
  const company = application.jobCompany ?? null;
  // TRACK-FIDELITY — null when the server's jobAvailable says the id no
  // longer resolves; see applicationJobHref's own doc comment for why this
  // is the ONE source of truth (never title/company presence as a proxy).
  const jobHref = applicationJobHref(application);
  // The follow-up draft button only makes sense for an 'applied' application
  // (composeFollowUpDraft.ts refuses anything else with not_yet_applied) —
  // this is a UX narrowing, not a NEW rule: the server itself would refuse
  // the exact same case with a served reason, so hiding the button here just
  // avoids a guaranteed round trip to a refusal the card can already predict
  // from the status it's holding. The button still appears (and can still
  // legitimately refuse for OTHER reasons: no letter on record, etc.) for
  // every 'applied' card, so the server's own honest refusal path is still
  // reachable and still rendered verbatim below.
  const canDraftFollowUp = application.status === 'applied';

  return (
    <Card className={clsx(styles.card, dragging && styles.dragging, pending && styles.pending)}>
      <div
        className={styles.dragHandle}
        draggable={!pending}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', application.id);
          e.dataTransfer.effectAllowed = 'move';
          onDragStart?.(application.id);
        }}
        onDragEnd={() => onDragEnd?.()}
        aria-label={`Drag "${title}" to a different stage`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="9" cy="6" r="1.4" fill="currentColor" />
          <circle cx="15" cy="6" r="1.4" fill="currentColor" />
          <circle cx="9" cy="12" r="1.4" fill="currentColor" />
          <circle cx="15" cy="12" r="1.4" fill="currentColor" />
          <circle cx="9" cy="18" r="1.4" fill="currentColor" />
          <circle cx="15" cy="18" r="1.4" fill="currentColor" />
        </svg>
      </div>

      {jobHref ? (
        <Link to={jobHref} className={styles.titleLink}>
          <div className={styles.title}>{title}</div>
          {company ? <div className={styles.meta}>{company}</div> : null}
        </Link>
      ) : (
        <div>
          <div className={styles.title}>{title}</div>
          {company ? <div className={styles.meta}>{company}</div> : null}
          <div className={styles.jobGone}>The full listing is no longer available.</div>
        </div>
      )}

      {silence ? (
        <div className={styles.silenceRow}>
          <span className={styles.silenceBadge} data-tier={application.nudgeTier}>
            {silence}
          </span>
        </div>
      ) : null}

      {offerClose ? (
        <div className={styles.closeOffer}>
          <span>Still quiet — mark this closed?</span>
          <button
            type="button"
            className={styles.closeOfferBtn}
            disabled={pending}
            onClick={() => onMoveStage(application.id, 'closed')}
          >
            Mark closed
          </button>
        </div>
      ) : null}

      <div className={styles.actionsRow}>
        <select
          aria-label={`Move ${title} to another stage`}
          className={styles.moveSelect}
          value={application.status}
          disabled={pending}
          onChange={(e) => onMoveStage(application.id, e.target.value as ApplicationStatus)}
        >
          {APPLICATION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {TRACKER_STAGE_DISPLAY_LABEL[s]}
            </option>
          ))}
        </select>

        {canDraftFollowUp ? (
          <button
            type="button"
            className={styles.actionBtn}
            disabled={draftingFollowUp}
            onClick={() => onDraftFollowUp(application.jobId)}
          >
            {draftingFollowUp ? 'Drafting…' : 'Draft follow-up'}
          </button>
        ) : null}
      </div>

      {pending ? <div className={styles.pendingNote}>Moving…</div> : null}

      {/* GAP 4 — the notes affordance: collapsed by default, expands to the
          existing VoiceTextarea (house rule #2: every textarea has a mic
          icon) + Save. A card that already has a real note starts expanded
          (see hasExistingNote above) so the user sees it without a tap. */}
      {!noteExpanded ? (
        <button
          type="button"
          className={styles.noteToggle}
          onClick={() => setNoteExpanded(true)}
        >
          {hasExistingNote ? `Note: “${truncateNotePreview(application.notes)}”` : 'Add a note'}
        </button>
      ) : (
        <div className={styles.noteEditor}>
          <VoiceTextarea
            label="Your note"
            value={noteDraft}
            onChange={setNoteDraft}
            placeholder="What happened, what you're thinking, anything worth remembering…"
            rows={3}
            disabled={savingNotes}
          />
          <div className={styles.noteActions}>
            <button
              type="button"
              className={styles.noteSaveBtn}
              disabled={savingNotes}
              onClick={handleSaveNote}
            >
              {savingNotes ? 'Saving…' : noteJustSaved ? 'Saved' : 'Save note'}
            </button>
            <button
              type="button"
              className={styles.noteCollapseBtn}
              disabled={savingNotes}
              onClick={() => {
                setNoteExpanded(hasExistingNote);
                setNoteDraft(application.notes ?? '');
                setNoteSaveError(null);
              }}
            >
              {hasExistingNote ? 'Done' : 'Cancel'}
            </button>
          </div>
          {noteSaveError ? (
            <div className={styles.noteSaveError} role="alert">
              {noteSaveError}
            </div>
          ) : null}
        </div>
      )}

      {draftResult ? <DraftResultNote result={draftResult} /> : null}
    </Card>
  );
}

/** A short, honest preview of the served note text for the collapsed toggle
 *  button — truncates on a word boundary rather than mid-word, never
 *  invents or paraphrases the note itself (the full text is always right
 *  there behind the expand tap). Returns the trimmed note verbatim when it
 *  already fits. */
export function truncateNotePreview(notes: string | null, maxLength = 40): string {
  const trimmed = (notes ?? '').trim();
  if (trimmed.length <= maxLength) return trimmed;
  const slice = trimmed.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  return `${(lastSpace > 10 ? slice.slice(0, lastSpace) : slice).trim()}…`;
}

/**
 * Renders POST /outreach/draft's SERVED result verbatim — the brief's own
 * law ("incl. the refusal reasons — render them verbatim"). Every branch
 * below prints a REAL server field (message/hint/gmailWebUrl), never a
 * client-paraphrased summary of what the server meant.
 */
function DraftResultNote({ result }: { result: DraftOutreachResult }) {
  if (result.ok) {
    return (
      <div className={clsx(styles.draftResult, styles.draftResultOk)}>
        {result.draft?.deliveredVia === 'resend' ? 'Follow-up drafted — check your email inbox to send it onward.' : 'Follow-up drafted to your Gmail Drafts.'}
        {result.draft.gmailWebUrl ? (
          <>
            {' '}
            <a
              className={styles.draftResultLink}
              href={result.draft.gmailWebUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open in Gmail
            </a>
          </>
        ) : null}
      </div>
    );
  }
  if (result.kind === 'follow_up_ungrounded') {
    return <div className={clsx(styles.draftResult, styles.draftResultRefusal)}>{result.message}</div>;
  }
  // gmail_not_connected / gmail_reconsent_required — both carry the server's
  // own `hint` verbatim.
  return <div className={clsx(styles.draftResult, styles.draftResultRefusal)}>{result.hint}</div>;
}
